// Loreto portal API client. Uses REACT_APP_BACKEND_URL with /api prefix.
import axios from 'axios';

// Empty/unset → relative "/api" (same-origin). Lets one build serve on any host
// (staging or prod) as long as that host routes /api to the backend.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API_BASE, timeout: 90000 });

// Attach X-Portal-Key from localStorage on every request when present, and
// the Git Timeline guest pass alongside it: a visitor with no account may open
// one repository of their own, and the pass is how the server recognises the
// browser that did. It rides on every request rather than the timeline ones
// only, because the pass is meaningless anywhere else and the alternative is a
// list of paths that goes stale.
client.interceptors.request.use((config) => {
  const key = localStorage.getItem('loreto_key');
  if (key) {
    config.headers = config.headers || {};
    if (!config.headers['X-Portal-Key']) config.headers['X-Portal-Key'] = key;
  }
  let pass = null;
  try { pass = localStorage.getItem('gt_guest_pass'); } catch (e) { /* private mode */ }
  if (pass) {
    config.headers = config.headers || {};
    if (!config.headers['X-Guest-Pass']) config.headers['X-Guest-Pass'] = pass;
  }
  return config;
});

// ── Portal auth ────────────────────────────────────────────────
export const portal = {
  plans: () => client.get('/portal/plans').then((r) => r.data),
  signup: (email, password, name) =>
    client.post('/portal/signup', { email, password, name }).then((r) => r.data),
  login: (email, password) =>
    client.post('/portal/login', { email, password }).then((r) => r.data),
  // One endpoint for both: Google hands back a verified address and the server
  // either matches an account or creates one. The browser cannot tell which,
  // and should not have to.
  google: (credential) =>
    client.post('/portal/google', { credential }).then((r) => r.data),
  googleConfig: () => client.get('/portal/google/config').then((r) => r.data),
  forgot: (email) =>
    client.post('/portal/forgot-password', { email }).then((r) => r.data),
  reset: (token, newPassword) =>
    client.post('/portal/reset-password', { token, new_password: newPassword }).then((r) => r.data),
  me: () => client.get('/portal/me').then((r) => r.data),
  keys: () => client.get('/portal/keys').then((r) => r.data),
  createKey: (label) => client.post('/portal/keys', { label }).then((r) => r.data),
  revokeKey: (id) => client.post(`/portal/keys/${id}/revoke`).then((r) => r.data),
  // Replace a key and reveal the new raw value. Works on Free, where createKey
  // is a paywall — the raw key is visible only in the response that mints it,
  // so this is the only way back for anyone who never captured theirs.
  rotateKey: (id) => client.post(`/portal/keys/${id}/rotate`).then((r) => r.data),
  usage: () => client.get('/portal/usage').then((r) => r.data),
  x402Info: () => client.get('/portal/x402/info').then((r) => r.data),
  myAgents: () => client.get('/portal/agents').then((r) => r.data),
  createAgent: (body) => client.post('/portal/agents', body).then((r) => r.data),
  updateAgent: (id, body) => client.patch(`/portal/agents/${id}`, body).then((r) => r.data),
  deleteAgent: (id) => client.delete(`/portal/agents/${id}`).then((r) => r.data),
  checkout: (plan) => client.post('/portal/checkout', { plan }).then((r) => r.data),
  confirmCheckout: (sessionId) =>
    client.post('/portal/checkout/confirm', { session_id: sessionId }).then((r) => r.data),

  // ── In-UI skill generation + per-user catalog ──────────────────
  // Generation is async now: these return quickly with {job_id, status:'pending'}.
  // Poll skillJob(job_id) until status flips to completed/failed.
  // githubToken travels as a header, never in the body: a body is what gets
  // logged, echoed back in errors and written to the job row. It is passed per
  // call and held nowhere — not in state that outlives the request, not in
  // localStorage, not in a URL.
  generate: (body, githubToken) =>
    client.post('/portal/skills/generate', body,
      githubToken ? { headers: { 'X-GitHub-Token': githubToken } } : undefined,
    ).then((r) => r.data),
  generateUpload: (formData) =>
    client.post('/portal/skills/generate/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  skillJobs: () => client.get('/portal/skills/jobs').then((r) => r.data),
  // Version N is the state the skill was in BEFORE edit N, so the list reads
  // as "what it used to be" and the live skill is always the default.
  skillVersions: (id) => client.get(`/portal/skills/${id}/versions`).then((r) => r.data),
  // Async like any other generation: 202 + a job to poll. The skill keeps
  // its id, so nothing linking to it needs updating.
  regenerateSkill: (id) => client.post(`/portal/skills/${id}/regenerate`).then((r) => r.data),
  skillVersion: (id, v) => client.get(`/portal/skills/${id}/versions/${v}`).then((r) => r.data),

  // GitHub App connection. No token ever crosses this boundary: connect returns
  // a URL to send the browser to, and the server mints a short-lived
  // installation token at fetch time from its own key.
  githubStatus: () => client.get('/portal/github/status').then((r) => r.data),
  githubConnect: () => client.post('/portal/github/connect').then((r) => r.data),
  // The same install flow, but GitHub sends the reader back to `path` (server
  // allow-listed) instead of the generator page.
  githubConnectFrom: (path) =>
    client.post(`/portal/github/connect?return_to=${encodeURIComponent(path)}`).then((r) => r.data),
  githubRepos: () => client.get('/portal/github/repos').then((r) => r.data),
  githubManage: () => client.get('/portal/github/manage').then((r) => r.data),
  githubDisconnect: () => client.post('/portal/github/disconnect').then((r) => r.data),
  // Answers "can we read this repository" before a generation is spent
  // finding out. { applicable: false } for anything that is not a GitHub URL.
  githubReachable: (url) =>
    client.get('/portal/github/reachable', { params: { url } }).then((r) => r.data),
  skillJob: (id) => client.get(`/portal/skills/jobs/${id}`).then((r) => r.data),
  mySkills: () => client.get('/portal/skills').then((r) => r.data),
  mySkill: (id) => client.get(`/portal/skills/${id}`).then((r) => r.data),
  deleteSkill: (id) => client.delete(`/portal/skills/${id}`).then((r) => r.data),
  // Returns the full axios response (blob in r.data) so the caller can save it.
  downloadSkill: (id) => client.get(`/portal/skills/${id}/download`, { responseType: 'blob' }),
};

// ── Marketplace ────────────────────────────────────────────────
export const marketplace = {
  rates: () => client.get('/marketplace/rates').then((r) => r.data),
  browse: (params = {}) => client.get('/marketplace/listings', { params }).then((r) => r.data),
  tags: () => client.get('/marketplace/tags').then((r) => r.data),
  personas: () => client.get('/marketplace/personas').then((r) => r.data),
  detail: (slug) => client.get(`/marketplace/listings/${slug}`).then((r) => r.data),
  reviews: (slug) => client.get(`/marketplace/listings/${slug}/reviews`).then((r) => r.data),
  leaderboard: (period) => client.get('/marketplace/leaderboard', { params: period ? { period } : {} }).then((r) => r.data),
  // Seller
  createListing: (body) => client.post('/portal/marketplace/listings', body).then((r) => r.data),
  uploadListing: (formData) => client.post('/portal/marketplace/listings/upload', formData).then((r) => r.data),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/portal/marketplace/upload-image', fd).then((r) => r.data);
  },
  myListings: () => client.get('/portal/marketplace/my-listings').then((r) => r.data),
  updateListing: (id, body) => client.patch(`/portal/marketplace/listings/${id}`, body).then((r) => r.data),
  deleteListing: (id) => client.delete(`/portal/marketplace/listings/${id}`).then((r) => r.data),
  metrics: () => client.get('/portal/marketplace/metrics').then((r) => r.data),
  // Buyer
  purchase: (id) => client.post(`/portal/marketplace/listings/${id}/purchase`).then((r) => r.data),
  confirmPurchase: (sessionId) =>
    client.post('/portal/marketplace/purchase/confirm', { session_id: sessionId }).then((r) => r.data),
  library: () => client.get('/portal/marketplace/library').then((r) => r.data),
  rate: (id, stars, comment) =>
    client.post(`/portal/marketplace/listings/${id}/rate`, { stars, comment }).then((r) => r.data),
  // Full axios response (blob in r.data) so the caller can save the zip.
  download: (id) => client.get(`/portal/marketplace/listings/${id}/download`, { responseType: 'blob' }),
};

// ── Profiles ───────────────────────────────────────────────────
export const profile = {
  update: (body) => client.patch('/portal/profile', body).then((r) => r.data),
  uploadAvatar: (formData) =>
    client.post('/portal/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  publicProfile: (username) => client.get(`/marketplace/users/${username}`).then((r) => r.data),
  creators: (params = {}) => client.get('/marketplace/creators', { params }).then((r) => r.data),
};

// ── Stripe Connect (seller payouts) ────────────────────────────
export const connect = {
  status: () => client.get('/portal/connect/status').then((r) => r.data),
  onboard: () => client.post('/portal/connect/onboard').then((r) => r.data),
  dashboard: () => client.post('/portal/connect/dashboard').then((r) => r.data),
};

// ── Messaging ──────────────────────────────────────────────────
export const messages = {
  threads: () => client.get('/portal/messages/threads').then((r) => r.data),
  unread: () => client.get('/portal/messages/unread').then((r) => r.data),
  // asId = the identity to read as (self, or an owned agent). Omitted → self.
  conversation: (otherId, asId) =>
    client.get(`/portal/messages/threads/${otherId}`, { params: asId ? { as_id: asId } : {} }).then((r) => r.data),
  // body may include as_agent_id to reply on an owned agent's behalf.
  send: (body) => client.post('/portal/messages', body).then((r) => r.data),
  markRead: (otherId, asId) =>
    client.post(`/portal/messages/threads/${otherId}/read`, null, { params: asId ? { as_id: asId } : {} }).then((r) => r.data),
};

// Absolute URL for a backend-served asset path like "/api/files/<id>".
// Build a URL for a stored asset. Pass `w` (px) to request a downscaled WebP
// thumbnail from the backend (/api/files/{id}?w=) — use for avatars so a large
// user upload isn't shipped at full resolution for a tiny circle.
// ── Git Timeline Film ($3 per repository) ───────────────────────
// Anonymous on purpose: the buyer's email comes back from Stripe. The portal
// key still rides along when present, which is what lets a private
// repository be read through the reader's GitHub connection.
export const timeline = {
  status: () => client.get('/timeline/status').then((r) => r.data),
  reachable: (repoUrl) => client.get('/timeline/reachable', { params: { url: repoUrl } }).then((r) => r.data),
  checkout: (repoUrl) => client.post('/timeline/checkout', { repo_url: repoUrl }).then((r) => r.data),
  createJob: (sessionId) => client.post('/timeline/jobs', { session_id: sessionId }).then((r) => r.data),
  job: (id) => client.get(`/timeline/jobs/${id}`).then((r) => r.data),
  // A frame of the reader's own repository, before any ask.
  preview: (repoUrl, options) => client.post('/timeline/preview', { repo_url: repoUrl, options: options || null }).then((r) => r.data),
  previewStatus: (id) => client.get(`/timeline/preview/${id}`).then((r) => r.data),
  // The one free film (720p, behind the paid queue) — needs a verified account.
  freeJob: (previewId) => client.post('/timeline/jobs/free', { preview_id: previewId }).then((r) => r.data),
  // The member film from the explorer: a window and options chosen by hand, on the plan's allowance.
  memberJob: (repoUrl, window, options) => client.post('/timeline/jobs/member', { repo_url: repoUrl, window, options: options || null }).then((r) => r.data),
  checkoutFor: (repoUrl, previewId, extra) =>
    client.post('/timeline/checkout', { repo_url: repoUrl, preview_id: previewId || null, ...(extra || {}) }).then((r) => r.data),
  requestVerification: (returnTo) =>
    client.post(`/portal/verify-email/request${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`).then((r) => r.data),
  confirmVerification: (token) => client.post('/portal/verify-email', { token }).then((r) => r.data),
  // The explorer: the whole history, built once per repo per week.
  explore: (repoUrl, options) => client.post('/timeline/explore', { repo_url: repoUrl, options: options || null }).then((r) => r.data),
  exploreStatus: (id) => client.get(`/timeline/explore/${id}`).then((r) => r.data),
  exploreScene: (id) => client.get(`/timeline/explore/${id}/scene.json`, { timeout: 180000 }).then((r) => r.data),
  exploreCommit: (id, h) => client.get(`/timeline/explore/${id}/commit/${h}`, { timeout: 120000 }).then((r) => r.data),
  // Phase 2: forensics on the build, and the investigator.
  exploreSearch: (id, q) => client.get(`/timeline/explore/${id}/search`, { params: { q }, timeout: 120000 }).then((r) => r.data),
  exploreFile: (id, path, opts = {}) => client.get(`/timeline/explore/${id}/file`, { params: { path, ...(opts.stats != null ? { stats: opts.stats } : {}) }, timeout: 180000 }).then((r) => r.data),
  exploreCoupling: (id, path) => client.get(`/timeline/explore/${id}/coupling`, { params: { path }, timeout: 180000 }).then((r) => r.data),
  exploreOwnership: (id, path) => client.get(`/timeline/explore/${id}/ownership`, { params: { path }, timeout: 120000 }).then((r) => r.data),
  // standing on the timeline: anon / free (a taste) / paid (films and Grain packages left)
  me: () => client.get('/timeline/me').then((r) => r.data),
  // the one free look for a visitor with no account, and the claim that keeps
  // it once they sign up
  guestPass: () => client.post('/timeline/explore/pass').then((r) => r.data),
  claimGuestBuilds: () => client.post('/timeline/explore/claim').then((r) => r.data),
  // Grain: the codebase's grain as agent files, laid out for a harness
  harnesses: () => client.get('/timeline/harnesses').then((r) => r.data),
  grainCreate: (id, body) => client.post(`/timeline/explore/${id}/grain`, body).then((r) => r.data),
  grainList: (id) => client.get(`/timeline/explore/${id}/grain`).then((r) => r.data),
  grainStatus: (id, gid) => client.get(`/timeline/explore/${id}/grain/${gid}`).then((r) => r.data),
  grainDelete: (id, gid) => client.delete(`/timeline/explore/${id}/grain/${gid}`).then((r) => r.data),
  exploreWindow: (id, from, to) => client.get(`/timeline/explore/${id}/window`, { params: { from, to }, timeout: 180000 }).then((r) => r.data),
  exploreChat: (id, messages, view, selected) =>
    client.post(`/timeline/explore/${id}/chat`, { messages, view, selected }, { timeout: 300000 }).then((r) => r.data),
};

// ── Field papers ───────────────────────────────────────────────
// The download is a header-authenticated file, so it cannot be a plain <a
// href> — the browser would send no portal key. Fetch it as a blob and hand
// the browser a synthetic link instead. Visitors arriving from the email have
// no session at all, only a signed token, which rides in the query string.
export const papers = {
  list: () => client.get('/papers').then((r) => r.data),
  get: (slug) => client.get(`/papers/${slug}`).then((r) => r.data),
  request: (slug, body) => client.post(`/papers/${slug}/request`, body).then((r) => r.data),
  // The bytes, fetched through the gate. `inline` only changes the
  // Content-Disposition the server sends, which matters when the blob is
  // handed to a viewer rather than to a save dialog.
  blob: async (slug, { token, inline } = {}) => {
    const params = {};
    if (token) params.t = token;
    if (inline) params.inline = 1;
    const r = await client.get(`/papers/${slug}/download`, {
      params: Object.keys(params).length ? params : undefined,
      responseType: 'blob',
    });
    // axios does not always carry the type through on a blob response, and a
    // PDF served as application/octet-stream will not render in a viewer.
    return r.data.type === 'application/pdf'
      ? r.data
      : new Blob([r.data], { type: 'application/pdf' });
  },
  download: async (slug, { token, filename } = {}) => {
    const url = URL.createObjectURL(await papers.blob(slug, { token }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `${slug}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick; Safari cancels the download if the URL dies
    // while it is still reading it.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
};

export const assetUrl = (path, w) => (path ? `${BACKEND_URL}${path}${w ? `?w=${w}` : ''}` : '');

// ── Skills proxy ───────────────────────────────────────────────
export async function generateSkills({ apiKey, source, sourceType, testLanguage, includeVisuals, context }) {
  const body = {
    source,
    source_type: sourceType || 'auto',
    test_language: testLanguage || 'python',
    include_visuals: includeVisuals !== false,
  };
  if (context && context.trim()) body.context = context.trim();
  // A key when one was typed; otherwise the browser session, so somebody who is
  // already signed in can Try It without hunting for a key they never saw.
  const session = localStorage.getItem('loreto_key');
  const auth = apiKey
    ? { 'X-API-Key': apiKey, Authorization: `Bearer ${apiKey}` }
    : { 'X-Portal-Key': session || '' };
  try {
    const r = await axios.post(`${API_BASE}/skills/generate`, body, {
      headers: {
        ...auth,
        'Content-Type': 'application/json',
      },
      timeout: 120000,
      validateStatus: () => true,
    });
    return { status: r.status, data: r.data };
  } catch (e) {
    return {
      status: 0,
      data: { error: 'Network error', detail: e.message || String(e) },
    };
  }
}

export default client;
